<template>
  <div class="therapist-view">
    <!-- Session Limit Error -->
    <div v-if="sessionLimitError" class="limit-banner">
      <div class="limit-banner-inner">
        <strong>{{ $t('controller.limitTitle') }}</strong>
        <p>{{ $t('controller.limitText') }}</p>
        <a href="mailto:info@expatpsychologie.nl" class="limit-link">{{ $t('controller.limitContact') }}</a>
      </div>
    </div>

    <!-- The therapist sees the exact same rendered stage as the patient -->
    <BallStage
      :speed="speed"
      :bounce-mode="bounceMode"
      :is-moving="isMoving"
      :ball-size="ballSize"
      :background-color="backgroundColor"
      :background-image="stageImage"
      :background-fit="imageFit"
      :ball-color="ballColor"
      :random-color="ballColorMode === 'random'"
      @turn="player.syncTurn($event)"
      @tempo="player.setTempo($event)"
    />

    <Dock>
      <!-- Play / Stop -->
      <DockButton
        :label="$t('controller.dock.play')"
        :expandable="false"
        :active="isMoving"
        @trigger="togglePlay"
      >
        <template #icon>
          <svg v-if="!isMoving" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
        </template>
      </DockButton>

      <!-- Movement: speed + pattern -->
      <DockButton :label="$t('controller.dock.movement')">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h4l3-9 6 18 3-9h4"/></svg>
        </template>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.speed') }}</label>
          <div class="range-wrap">
            <input type="range" min="1" max="80" v-model.number="speed" @input="updateBall" />
            <span class="range-value">{{ speed }}</span>
          </div>
        </div>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.pattern') }}</label>
          <select v-model="bounceMode" @change="updateBall" class="select-field">
            <option value="horizontal">{{ $t('controller.dock.patternHorizontal') }}</option>
            <option value="vertical">{{ $t('controller.dock.patternVertical') }}</option>
            <option value="random">{{ $t('controller.dock.patternRandom') }}</option>
            <option value="figure8">{{ $t('controller.dock.patternFigure8') }}</option>
          </select>
        </div>
      </DockButton>

      <!-- Visual: background, ball color, ball size -->
      <DockButton :label="$t('controller.dock.visual')">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </template>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.ballSize') }}</label>
          <div class="range-wrap">
            <input type="range" min="10" max="80" v-model.number="ballSize" @input="updateBallSize" />
            <span class="range-value">{{ ballSize }}px</span>
          </div>
        </div>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.background') }}</label>
          <div class="color-input-wrap">
            <input type="color" v-model="backgroundColor" @input="updateBackground" class="color-input" />
            <span class="color-hex">{{ backgroundColor }}</span>
          </div>
        </div>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.ballColor') }}</label>
          <div class="ball-color-row">
            <select v-model="ballColorMode" @change="updateBallColorMode" class="select-field select-sm">
              <option value="static">{{ $t('controller.dock.colorStatic') }}</option>
              <option value="random">{{ $t('controller.dock.colorRandom') }}</option>
            </select>
            <div v-if="ballColorMode === 'static'" class="color-input-wrap">
              <input type="color" v-model="ballColor" @input="updateBallColor" class="color-input" />
              <span class="color-hex">{{ ballColor }}</span>
            </div>
          </div>
        </div>
      </DockButton>

      <!-- Background image: preset scenes or the therapist's own upload -->
      <DockButton :label="$t('controller.dock.image')" :active="!!bgImage">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        </template>
        <div class="preset-scroll">
          <div v-for="group in presetGroups" :key="group.id" class="popover-field">
            <label class="field-label">{{ $t(group.labelKey) }}</label>
            <div class="preset-grid">
              <button
                v-for="preset in group.items"
                :key="preset.id"
                type="button"
                class="preset-thumb"
                :class="{ 'preset-selected': bgImage && bgImage.kind === 'preset' && bgImage.id === preset.id }"
                :style="{ backgroundImage: `url(${presetUrl(preset.id, true)})` }"
                :title="presetTitle(preset)"
                :aria-label="$t(`controller.scenes.${preset.id}`)"
                @click="selectPreset(preset.id)"
              ></button>
            </div>
          </div>
        </div>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.ownImage') }}</label>
          <template v-if="loggedIn">
            <input ref="imageInput" type="file" accept="image/*" class="file-input" @change="onImageFile" />
            <button type="button" class="btn-copy-sm" :disabled="uploading" @click="$refs.imageInput.click()">
              {{ uploading ? $t('controller.dock.uploading') : $t('controller.dock.uploadImage') }}
            </button>
            <p class="field-hint">{{ $t('controller.dock.imagePrivacy') }}</p>
          </template>
          <!-- Opens in a new tab so the running session isn't interrupted;
               this tab unlocks by itself once the login lands in localStorage -->
          <div v-else class="login-placeholder">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <div>
              <p class="field-hint">{{ $t('controller.dock.ownImageLoginOnly') }}</p>
              <a :href="loginUrl" target="_blank" rel="noopener" class="login-link">{{ $t('controller.dock.ownImageLogin') }}</a>
            </div>
          </div>
          <p v-if="imageError" class="field-error">{{ imageError }}</p>
        </div>
        <div v-if="bgImage" class="popover-field">
          <label class="field-label">{{ $t('controller.dock.imageFit') }}</label>
          <select v-model="imageFit" @change="updateImageFit" class="select-field">
            <option value="cover">{{ $t('controller.dock.fitCover') }}</option>
            <option value="contain">{{ $t('controller.dock.fitContain') }}</option>
          </select>
          <button type="button" class="btn-ghost-sm" @click="removeImage">{{ $t('controller.dock.removeImage') }}</button>
        </div>
      </DockButton>

      <!-- Bilateral audio: on/off and sound type -->
      <DockButton :label="$t('controller.dock.sound')" :active="bilateralSoundActive">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path v-if="bilateralSoundActive" d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
        </template>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.soundPatient') }}</label>
          <button type="button" class="btn-copy-sm" @click="toggleBilateralSound">
            {{ bilateralSoundActive ? $t('controller.dock.soundOff') : $t('controller.dock.soundOn') }}
          </button>
        </div>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.soundType') }}</label>
          <div class="sound-row">
            <select v-model="soundId" @change="updateSoundType" class="select-field">
              <option v-for="sound in sounds" :key="sound.id" :value="sound.id">{{ $t(sound.labelKey) }}</option>
            </select>
            <button type="button" class="btn-ghost-sm" @click="previewSound">{{ $t('controller.dock.preview') }}</button>
          </div>
        </div>
        <div class="popover-field">
          <label class="check-row">
            <input type="checkbox" v-model="hearLocally" @change="onHearLocallyChange" />
            <span>{{ $t('controller.dock.hearLocally') }}</span>
          </label>
          <p class="field-hint">{{ $t('controller.dock.hearLocallyHint') }}</p>
        </div>
      </DockButton>

      <!-- Dossier notes (only when the session is linked to one of the therapist's patients) -->
      <DockButton v-if="patient" :label="$t('controller.dock.notes')">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
        </template>
        <div class="popover-field notes-popover">
          <label class="field-label">{{ $t('controller.dock.notesFor') }} {{ patient.name }}</label>
          <textarea
            v-model="newNote"
            rows="3"
            class="note-textarea"
            maxlength="10000"
            :placeholder="$t('controller.dock.notePlaceholder')"
          ></textarea>
          <button type="button" class="btn-copy-sm" :disabled="noteBusy || !newNote.trim()" @click="addSessionNote">
            {{ $t('controller.dock.addNote') }}
          </button>
          <p v-if="noteError" class="field-error">{{ noteError }}</p>
          <div class="notes-recent">
            <p v-if="!notes.length" class="field-hint">{{ $t('controller.dock.noNotes') }}</p>
            <div v-for="n in notes.slice(0, 5)" :key="n.id" class="note-item-sm">
              <span class="note-date-sm">{{ formatNoteDate(n.created) }}</span>
              <p>{{ n.note }}</p>
            </div>
          </div>
        </div>
      </DockButton>

      <!-- Share patient link -->
      <DockButton :label="$t('controller.dock.share')">
        <template #icon>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        </template>
        <div class="popover-field">
          <label class="field-label">{{ $t('controller.dock.patientLink') }}</label>
          <a :href="patientUrl" target="_blank" class="share-link">{{ fullPatientUrl }}</a>
          <button class="btn-copy-sm" @click="copyPatientUrl">{{ copyLabel }}</button>
        </div>
      </DockButton>
    </Dock>
  </div>
</template>

<script>
import axios from 'axios';
import BallStage from './components/BallStage.vue';
import Dock from './components/Dock.vue';
import DockButton from './components/DockButton.vue';
import { applySeo } from './i18n/head';
import { SOUNDS, DEFAULT_SOUND, BilateralPlayer } from './sounds';
import { authState, api, isLoggedIn } from './auth';
import { PRESET_GROUPS, presetUrl, prepareUpload } from './backgrounds';
import { newSessionId } from './sessionId';
import { localeInfo } from './i18n/locales';

const HEAR_LOCALLY_KEY = 'emdr-therapist-hear-sound';

function readHearLocally() {
  try {
    return localStorage.getItem(HEAR_LOCALLY_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export default {
  components: { BallStage, Dock, DockButton },
  data() {
    return {
      speed: 5,
      sessionLimitError: false,
      bounceMode: 'horizontal',
      backgroundColor: '#ffffff',
      ballColor: '#2196f3',
      ballColorMode: 'static',
      isMoving: false,
      sessionId: '',
      ballSize: 30,
      bilateralSoundActive: false,
      soundId: DEFAULT_SOUND,
      sounds: SOUNDS,
      // Whether the therapist also hears the bilateral sound locally
      hearLocally: readHearLocally(),
      presetGroups: PRESET_GROUPS,
      // Background image as last confirmed by the server
      bgImage: null,
      imageFit: 'cover',
      // This tab's own upload: { id, dataUrl }. Also mirrored to sessionStorage
      // so a page reload can restore it; closing the tab discards it.
      localImage: null,
      uploading: false,
      bgBusy: 0,
      imageError: '',
      fetchInterval: null,
      copyLabel: this.$t('controller.dock.copy'),
      // Dossier notes for the patient this session belongs to (logged-in only)
      patient: null,
      notes: [],
      newNote: '',
      noteBusy: false,
      noteError: ''
    };
  },
  computed: {
    patientUrl() {
      return `${this.$localePath('/patient')}?session=${this.sessionId}`;
    },
    fullPatientUrl() {
      return window.location.origin + this.patientUrl;
    },
    loggedIn() {
      return !!authState.token;
    },
    loginUrl() {
      return this.$localePath('/login');
    },
    sessionParam() {
      return this.sessionId ? `?session=${encodeURIComponent(this.sessionId)}` : '';
    },
    storageKey() {
      return `emdr-bg-${this.sessionId}`;
    },
    stageImage() {
      const image = this.bgImage;
      if (!image) return '';
      if (image.kind === 'preset') return presetUrl(image.id);
      if (this.localImage && this.localImage.id === image.id) return this.localImage.dataUrl;
      return `/api/background/image${this.sessionParam}&v=${image.id}`;
    }
  },
  watch: {
    bilateralSoundActive() {
      this.syncLocalSound();
    },
    soundId() {
      this.syncLocalSound();
    },
    loggedIn(isLoggedIn) {
      // Own images are a logged-in feature: take ours down on logout
      if (!isLoggedIn && this.localImage) {
        this.removeImage();
      }
    }
  },
  created() {
    // Kept out of data() so Vue doesn't make the AudioContext reactive
    this.player = new BilateralPlayer();
  },
  mounted() {
    applySeo({
      locale: this.$locale,
      routeBase: 'controller',
      title: this.$t('controller.meta.title'),
      description: this.$t('controller.meta.description'),
      noindex: true
    });
    this.getSessionIdFromUrl();
    this.startSync();
    window.addEventListener('pagehide', this.releaseUploadedImage);
    if (this.hearLocally) {
      // Browsers block audio until the user interacts with the page
      document.addEventListener('pointerdown', this.unlockLocalSound, { once: true, capture: true });
    }
  },
  beforeDestroy() {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
    }
    window.removeEventListener('pagehide', this.releaseUploadedImage);
    this.releaseUploadedImage();
    document.removeEventListener('pointerdown', this.unlockLocalSound, { capture: true });
    this.player.close();
  },
  methods: {
    startSync() {
      this.restoreLocalImage();
      this.loadSessionPatient();
      this.fetchState();
      this.fetchInterval = setInterval(this.fetchState, 3000);
    },
    async loadSessionPatient() {
      if (!isLoggedIn()) return;
      try {
        const { data } = await api.get(`/api/sessions/${encodeURIComponent(this.sessionId)}/patient`);
        this.patient = data.patient || null;
        if (this.patient) this.loadNotes();
      } catch (e) {
        // Not linked to a patient, or request failed - just no notes panel
      }
    },
    async loadNotes() {
      if (!this.patient) return;
      try {
        const { data } = await api.get(`/api/patients/${this.patient.id}/notes`);
        this.notes = data.notes;
      } catch (e) {
        // ignore
      }
    },
    async addSessionNote() {
      const text = this.newNote.trim();
      if (!text || !this.patient) return;
      this.noteBusy = true;
      this.noteError = '';
      try {
        await api.post(`/api/patients/${this.patient.id}/notes`, { note: text });
        this.newNote = '';
        await this.loadNotes();
      } catch (e) {
        this.noteError = this.$t('controller.dock.noteError');
      } finally {
        this.noteBusy = false;
      }
    },
    formatNoteDate(ts) {
      return new Date(ts * 1000).toLocaleString(
        localeInfo(this.$locale).dateLocale,
        { dateStyle: 'short', timeStyle: 'short' }
      );
    },
    getSessionIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      let sessionId = params.get('session');
      if (!sessionId) {
        // Create a new session ID for the therapist
        sessionId = newSessionId();
        // Update URL without reloading
        const newUrl = `${window.location.pathname}?session=${sessionId}`;
        window.history.replaceState({}, '', newUrl);
      }
      this.sessionId = sessionId;
    },
    copyPatientUrl() {
      navigator.clipboard.writeText(this.fullPatientUrl).then(() => {
        this.copyLabel = this.$t('controller.dock.copied');
        setTimeout(() => {
          this.copyLabel = this.$t('controller.dock.copy');
        }, 1500);
      });
    },
    togglePlay() {
      this.isMoving = !this.isMoving;
      this.updateBall();
      if (!this.isMoving && this.bilateralSoundActive) {
        this.toggleBilateralSound();
      }
    },
    toggleBilateralSound() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      const newState = !this.bilateralSoundActive;
      axios.post(`/api/sound${sessionParam}`, { bilateral: newState, sound: this.soundId })
        .then(() => {
          this.bilateralSoundActive = newState;
        })
        .catch(err => console.error('Error toggling bilateral sound:', err));
    },
    updateSoundType() {
      axios.post(`/api/sound${this.sessionParam}`, { sound: this.soundId })
        .catch(err => console.error('Error updating sound type:', err));
    },
    previewSound() {
      this.player.preview(this.soundId);
    },
    onHearLocallyChange() {
      try {
        localStorage.setItem(HEAR_LOCALLY_KEY, this.hearLocally ? '1' : '0');
      } catch (e) {
        // ignore
      }
      if (this.hearLocally) {
        this.player.unlock();
      }
      this.syncLocalSound();
    },
    unlockLocalSound() {
      if (this.hearLocally) {
        this.player.unlock();
        this.syncLocalSound();
      }
    },
    // Local playback follows the session's sound on/off switch, but only
    // when the therapist opted in
    syncLocalSound() {
      this.player.setSound(this.soundId);
      if (this.hearLocally && this.bilateralSoundActive && this.player.unlocked) {
        this.player.start();
      } else {
        this.player.stop();
      }
    },
    presetUrl,
    presetTitle(preset) {
      const name = this.$t(`controller.scenes.${preset.id}`);
      return preset.credit ? `${name} — ${preset.credit} / Unsplash` : name;
    },
    async onImageFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      this.imageError = '';
      this.uploading = true;
      try {
        const { blob, dataUrl } = await prepareUpload(file);
        await this.uploadImage(blob, dataUrl);
      } catch (err) {
        const status = err.response && err.response.status;
        const key = err.message === 'decode' ? 'imageErrorDecode'
          : err.message === 'too-large' ? 'imageErrorLarge'
            : status === 401 ? 'imageErrorLogin' : 'imageErrorUpload';
        this.imageError = this.$t(`controller.dock.${key}`);
      } finally {
        this.uploading = false;
      }
    },
    async uploadImage(blob, dataUrl) {
      this.bgBusy++;
      try {
        const res = await api.post(
          `/api/background/image${this.sessionParam}&fit=${this.imageFit}`,
          blob,
          { headers: { 'Content-Type': 'image/jpeg' } }
        );
        this.localImage = { id: res.data.image.id, dataUrl };
        this.bgImage = res.data.image;
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ dataUrl, fit: this.imageFit }));
        } catch (e) {
          // Storage full or blocked: the image just won't survive a reload
        }
      } finally {
        this.bgBusy--;
      }
    },
    // After a reload, re-send this tab's image (the unload beacon has
    // already removed it from the server)
    async restoreLocalImage() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      } catch (e) {
        return;
      }
      if (!saved || !saved.dataUrl) return;
      if (!this.loggedIn) {
        this.forgetLocalImage();
        return;
      }
      this.imageFit = saved.fit || 'cover';
      this.localImage = { id: null, dataUrl: saved.dataUrl };
      await this.reuploadLocalImage();
    },
    async reuploadLocalImage() {
      if (this.uploading || !this.localImage) return;
      this.uploading = true;
      try {
        const blob = await (await fetch(this.localImage.dataUrl)).blob();
        await this.uploadImage(blob, this.localImage.dataUrl);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          // Logged out meanwhile: don't keep retrying
          this.forgetLocalImage();
        } else {
          console.error('Error restoring background image:', err);
        }
      } finally {
        this.uploading = false;
      }
    },
    forgetLocalImage() {
      this.localImage = null;
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        // ignore
      }
    },
    // Deletes this tab's upload from server memory. sendBeacon still gets
    // through while the page is closing, unlike a normal request.
    releaseUploadedImage() {
      if (!this.localImage || !this.localImage.id) return;
      navigator.sendBeacon(`/api/background/image/clear${this.sessionParam}&id=${this.localImage.id}`);
    },
    postBackgroundImage(payload) {
      this.bgBusy++;
      return axios.post(`/api/background${this.sessionParam}`, payload)
        .then(res => {
          this.bgImage = res.data.image;
        })
        .catch(err => console.error('Error updating background image:', err))
        .finally(() => {
          this.bgBusy--;
        });
    },
    selectPreset(id) {
      this.imageError = '';
      this.forgetLocalImage();
      this.postBackgroundImage({ image: { kind: 'preset', id, fit: this.imageFit } });
    },
    updateImageFit() {
      if (this.localImage) {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ dataUrl: this.localImage.dataUrl, fit: this.imageFit }));
        } catch (e) {
          // ignore
        }
      }
      this.postBackgroundImage({ fit: this.imageFit });
    },
    removeImage() {
      this.imageError = '';
      this.forgetLocalImage();
      this.postBackgroundImage({ image: null });
    },
    syncBackgroundImage(image) {
      // Don't let a poll that raced an in-flight change overwrite it
      if (this.bgBusy || this.uploading) return;
      if (!image && this.localImage) {
        // Server dropped our upload (e.g. idle purge) while this tab is
        // still open: send it again
        this.reuploadLocalImage();
        return;
      }
      if (image && this.localImage && !(image.kind === 'upload' && image.id === this.localImage.id)) {
        // Changed from another tab: that choice wins
        this.forgetLocalImage();
      }
      this.bgImage = image;
      if (image) {
        this.imageFit = image.fit || 'cover';
      }
    },
    updateBall() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      axios.post(`/api/ball${sessionParam}`, { speed: this.speed, bounceMode: this.bounceMode, isMoving: this.isMoving, ballSize: this.ballSize })
        .catch(err => console.error('Error updating ball:', err));
    },
    updateBallSize() {
      this.updateBall();
    },
    updateBackground() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      axios.post(`/api/background${sessionParam}`, { backgroundColor: this.backgroundColor })
        .catch(err => console.error('Error updating background:', err));
    },
    updateBallColor() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      axios.post(`/api/ballcolor${sessionParam}`, { ballColor: this.ballColor, randomColor: false })
        .catch(err => console.error('Error updating ball color:', err));
    },
    updateBallColorMode() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      if (this.ballColorMode === 'random') {
        axios.post(`/api/ballcolor${sessionParam}`, { randomColor: true })
          .catch(err => console.error('Error updating ball color mode:', err));
      } else {
        axios.post(`/api/ballcolor${sessionParam}`, { ballColor: this.ballColor, randomColor: false })
          .catch(err => console.error('Error updating ball color mode:', err));
      }
    },
    // Periodically re-sync from the backend in case the session was
    // already running (e.g. therapist reloaded the page).
    fetchState() {
      const sessionParam = this.sessionId ? `?session=${this.sessionId}` : '';
      axios.get(`/api/ball${sessionParam}`).then(res => {
        if (res.data.error && res.data.message) {
          this.sessionLimitError = true;
          return;
        }
        this.sessionLimitError = false;
        this.speed = res.data.speed;
        this.bounceMode = res.data.bounceMode || 'horizontal';
        this.isMoving = res.data.isMoving || false;
        if (res.data.ballSize !== undefined) {
          this.ballSize = res.data.ballSize;
        }
      });
      axios.get(`/api/background${sessionParam}&role=therapist`).then(res => {
        this.backgroundColor = res.data.backgroundColor || '#ffffff';
        this.syncBackgroundImage(res.data.image || null);
      });
      axios.get(`/api/ballcolor${sessionParam}`).then(res => {
        this.ballColor = res.data.ballColor || '#2196f3';
        this.ballColorMode = res.data.randomColor ? 'random' : 'static';
      });
      axios.get(`/api/sound${sessionParam}`).then(res => {
        this.bilateralSoundActive = res.data.bilateral === true;
        this.soundId = res.data.sound || DEFAULT_SOUND;
      });
    }
  }
};
</script>

<style scoped>
.therapist-view {
  position: relative;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

/* ===== LIMIT BANNER ===== */
.limit-banner {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  max-width: 90vw;
}
.limit-banner-inner {
  background: linear-gradient(135deg, #c0392b 0%, #e67e22 100%);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  color: white;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}
.limit-banner-inner p {
  margin: 0.5rem 0;
  opacity: 0.9;
  font-size: 0.9rem;
}
.limit-link {
  color: white;
  font-weight: 600;
  text-decoration: underline;
}

/* ===== POPOVER FIELDS (shared look for all Dock popovers) ===== */
.popover-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.01em;
}
.range-wrap {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.range-value {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--c-accent-light, #D4AB6A);
  background: rgba(255, 255, 255, 0.1);
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  min-width: 36px;
  text-align: center;
  flex-shrink: 0;
}
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.16);
  outline: none;
  cursor: pointer;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--c-accent, #C4964A);
  cursor: pointer;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}
input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--c-accent, #C4964A);
  cursor: pointer;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}
.select-field {
  padding: 0.45rem 0.65rem;
  font-family: var(--ff-body, 'Inter', sans-serif);
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  outline: none;
}
.select-field option {
  color: var(--c-text, #2D3436);
  background: white;
}
.select-sm {
  flex-shrink: 0;
  width: auto;
}
.ball-color-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.color-input-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.color-input {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  background: none;
}
.color-hex {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
  font-family: monospace;
}
.share-link {
  font-size: 0.8rem;
  color: var(--c-accent-light, #D4AB6A);
  word-break: break-all;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  padding-bottom: 2px;
}
.share-link:hover {
  color: var(--c-accent, #C4964A);
}
.btn-copy-sm {
  align-self: flex-start;
  background: var(--c-accent, #C4964A);
  color: #1A2F2C;
  border: none;
  border-radius: 50px;
  padding: 0.4rem 1rem;
  font-family: var(--ff-body, 'Inter', sans-serif);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.btn-copy-sm:hover {
  background: var(--c-accent-light, #D4AB6A);
}
.btn-copy-sm:disabled {
  opacity: 0.6;
  cursor: default;
}
.btn-ghost-sm {
  align-self: flex-start;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50px;
  padding: 0.35rem 0.9rem;
  font-family: var(--ff-body, 'Inter', sans-serif);
  font-size: 0.8rem;
  cursor: pointer;
  flex-shrink: 0;
}
.btn-ghost-sm:hover {
  background: rgba(255, 255, 255, 0.1);
}
.sound-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.sound-row .select-field {
  flex: 1;
}
.file-input {
  display: none;
}
.login-placeholder {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 0.65rem 0.75rem;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.7);
}
.login-placeholder svg {
  flex-shrink: 0;
  margin-top: 1px;
}
.login-placeholder .field-hint {
  margin-bottom: 0.35rem;
}
.login-link {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--c-accent-light, #D4AB6A);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.login-link:hover {
  color: var(--c-accent, #C4964A);
}
.check-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}
.check-row input {
  width: 16px;
  height: 16px;
  accent-color: var(--c-accent, #C4964A);
  cursor: pointer;
}
.field-hint {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.55);
}
.field-error {
  margin: 0;
  font-size: 0.75rem;
  color: #f0a39d;
}
.notes-popover {
  width: 280px;
  max-width: 80vw;
}
.note-textarea {
  width: 100%;
  padding: 0.5rem 0.65rem;
  font-family: var(--ff-body, 'Inter', sans-serif);
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.92);
  outline: none;
  resize: vertical;
}
.note-textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}
.notes-recent {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: min(32vh, 260px);
  overflow-y: auto;
  margin-top: 0.2rem;
}
.note-item-sm {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
}
.note-item-sm p {
  margin: 0.2rem 0 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.9);
  white-space: pre-wrap;
}
.note-date-sm {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.45);
}
.preset-scroll {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 300px;
  max-width: 80vw;
  max-height: min(40vh, 340px);
  overflow-y: auto;
  padding-right: 4px;
}
.preset-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
}
.preset-thumb {
  aspect-ratio: 16 / 9;
  border: 2px solid transparent;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  padding: 0;
}
.preset-thumb.preset-selected {
  border-color: var(--c-accent, #C4964A);
}
</style>

